<template lang="pug">
.resident-status-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :filter-included-fields="filterFields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // TODO ここから下の列情報はFamilySafetyConfirmMasterのレコードが増えるたびに対応する必要がある
    // ヘッダー：
    template(#head(residentStatus)="header")
      .confirm-header.d-flex.flex-row.justify-content-center.align-items-center
        span.mr-1 ご利用者様の状況
        span(v-if="header.field.required == true").validation-label 必須
    template(#head(residentLocation)="header")
      .confirm-header.d-flex.flex-row.justify-content-center.align-items-center
        span.mr-1 ご利用者様の所在地
        span(v-if="header.field.required == true").validation-label 必須
    template(#head(hospitalizationStatus)="header")
      .confirm-header.d-flex.flex-row.justify-content-center.align-items-center
        span.mr-1 診察／入院状況
        span(v-if="header.field.required == true").validation-label 必須
    template(#head(residentRoom)="header")
      .confirm-header.d-flex.flex-row.justify-content-center.align-items-center
        span.mr-1 ご利用者様の居室
        span(v-if="header.field.required == true").validation-label 必須
    template(#head(residentMessage)="header")
      .confirm-header.d-flex.flex-row.justify-content-center.align-items-center
        span.mr-1 連絡事項
        span(v-if="header.field.required == true").validation-label 必須

    // セル：部屋名・利用者名
    template(#cell(residentRoomAndName)="row")
      .room-name {{ row.item.room }}
      .resident-name {{ row.item.residentName }}
    // TODO ここから下の行情報はFamilySafetyConfirmMasterのレコードが増えるたびに対応する必要がある
    // セル：ご利用者様の状況
    template(#cell(residentStatus)="row")
      FormInputResidentStatusRadio(v-model="row.item.status.residentStatus.answer"
        :options="getOptions(row.item, 'residentStatus')"
        :keyName="getKeyName(row.item, 'residentStatus')"
        :name="'resident-status-' + row.item.residentId"
        :validationRules="{ required: getRequired(row.item, 'residentStatus') }"
        :required="getRequired(row.item, 'residentStatus')")
    // セル：ご利用者様の所在地
    template(#cell(residentLocation)="row")
      FormInputResidentStatusRadio(v-model="row.item.status.residentLocation.answer"
        :options="getOptions(row.item, 'residentLocation')"
        :keyName="getKeyName(row.item, 'residentLocation')"
        :name="'resident-location-' + row.item.residentId"
        :validationRules="{ required: getRequired(row.item, 'residentLocation') }"
        :required="getRequired(row.item, 'residentLocation')")
    // セル：診察／入院状況
    template(#cell(hospitalizationStatus)="row")
      FormInputResidentStatusRadio(v-model="row.item.status.hospitalizationStatus.answer"
        :options="getOptions(row.item, 'hospitalizationStatus')"
        :keyName="getKeyName(row.item, 'hospitalizationStatus')"
        :name="'hospitalization-status-' + row.item.residentId"
        :validationRules="{ required: getRequired(row.item, 'hospitalizationStatus') }"
        :required="getRequired(row.item, 'hospitalizationStatus')")
    // セル：ご利用者様の居室
    template(#cell(residentRoom)="row")
      FormInputResidentStatusRadio(v-model="row.item.status.residentRoom.answer"
        :options="getOptions(row.item, 'residentRoom')"
        :keyName="getKeyName(row.item, 'residentRoom')"
        :name="'resident-room-' + row.item.residentId"
        :validationRules="{ required: getRequired(row.item, 'residentRoom') }"
        :required="getRequired(row.item, 'residentRoom')")
    // セル：連絡事項
    template(#cell(residentMessage)="row")
      .register-row
        .register-group
          .register-value 
            textarea.register-form(
              v-model="row.item.status.residentMessage.answer"
              :name="'resident-message-' + row.item.residentId"
              :placeholder="getPlaceholder(row.item, 'residentMessage')"
              v-validate="{ max: getMaxlength(row.item, 'residentMessage'), required: getRequired(row.item, 'residentMessage') }"
              :maxlength="getMaxlength(row.item, 'residentMessage')"
              :data-vv-as="getKeyName(row.item, 'residentMessage')"
              :style="{ minHeight: '160px' }"
              :class="{ noinput: (getRequired(row.item, 'residentMessage') && !_value) }")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";

import { FamilySafetyConfirmMasterMultipleTextInputOption as TextInputOption } from "@/commons/services/FamilySafetyConfirmMasters";
import FormInputResidentStatusRadio from "@/pc/components/forms/FormInputResidentStatusRadio.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import { Status } from "./ResidentStatusTab.vue";

/**
 * ### 利用者個別情報テーブルコンポーネント
 */
@Component({
  components: {
    FormInputResidentStatusRadio,
    FormTextarea
  }
})
export default class ResidentStatusTable extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ResidentStatusTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ResidentStatusTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** フィルター対象カラム */
  @Prop({ required: true })
  filterFields!: string[];

  get _value(): Array<ResidentStatusTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: ResidentStatusTableItem): boolean {
    if (row.groupId == this.filter) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  public getModel(item: ResidentStatusTableItem, name: string): any {
    return item.status[`${name}`].answer;
  }

  public getOptions(item: ResidentStatusTableItem, name: string): any {
    return item.status[`${name}`].options;
  }

  public getKeyName(item: ResidentStatusTableItem, name: string): any {
    return item.status[`${name}`].label;
  }

  public getRequired(item: ResidentStatusTableItem, name: string): any {
    return item.status[`${name}`].required;
  }

  public getPlaceholder(item: ResidentStatusTableItem, name: string): any {
    const inputOptions: TextInputOption = item.status[`${name}`].inputOptions as TextInputOption;
    return inputOptions.placeholder;
  }

  public getMaxlength(item: ResidentStatusTableItem, name: string): any {
    const inputOptions: TextInputOption = item.status[`${name}`].inputOptions as TextInputOption;
    return inputOptions.maxLength;
  }
}

/** テーブルの列名の情報 */
export interface ResidentStatusTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
  required: boolean;
}

/** テーブルの1行あたりの情報 */
export interface ResidentStatusTableItem {
  facilityId: number;
  groupId: number;
  groupName: string;
  residentId: number;
  room: string;
  residentName: string;
  status: ResidentStatusItemStatus;
  updated?: number;
}

export interface ResidentStatusItemStatus {
  [key: string]: Status;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.resident-status-table-component {
  // テーブルがタブの横幅を超えるため、サイズ指定をする必要がある
  width: 100%;

  .validation-label {
    background-color: $bg-default;
    @include validation-label();
    margin: 0 0 2px 0;
  }

  .register-row {
    margin: 0;
    .register-group {
      margin: 0;
    }
  }
}
</style>
